import { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDialogFocus } from '../../hooks/useDialogFocus';
import { cancelRingNotification } from '../../services/incoming-call-notification';
import { acceptRing, declineRing } from '../../services/ring.service';
import { useActiveCohortSessionsStore } from '../../stores/active-cohort-sessions.store';
import { useEmpathyQueueStore } from '../../stores/empathy-queue.store';
import { LIVE_CALL_ROUTE, useRingStore } from '../../stores/ring.store';
import { withSession } from '../../stores/session-read';
import type { PendingRing } from '../../types/ring';
import { Z_RING } from '../../utils/layers';

/** User-facing copy, in one place so a test can read every string (D56: never "on call"). */
export const RING_COPY = {
  // The general queue (a race: several listeners rung, the first to accept
  // takes it). The seeker stays unnamed.
  title: 'Someone needs empathy now',
  platform: 'Someone on EmpathEase would love some empathy.',
  why: "You're available, so we're ringing you. The first listener to accept takes the call.",
  // A group request (David 2026-10-19 16:08: usually not a race). Title and
  // line are the ring's push (EmpathyRequestLadder::ringCopy); the request's
  // own words are quoted on this sheet only, never in the push (18:40).
  groupTitle: (name: string, group: string) => `${name} asked ${group} for empathy`,
  groupBody: 'Connect with them?',
  groupWhy: (name: string) => `If ${name} is already being heard, this closes on its own.`,
  accept: 'Accept',
  accepting: 'Connecting…',
  notNow: 'Not now',
  close: 'Close',
} as const;

interface Props {
  ring: PendingRing;
  /** A gate (agreement, norms, upgrade) is up: the ring waits behind it, still mounted. */
  hidden: boolean;
}

/**
 * The in-app ring (P7d): "Someone needs empathy now" (the general queue) or
 * "{name} asked {group} for empathy" (a group request) · Accept / Not now.
 *
 * Accept is the same call path the push tap leads to (the claim / match
 * path, server-side); on success the 1:1 tab opens the call. A refusal
 * (someone else took it, they no longer need a listener, a gate the user
 * declined) is shown in place with Close (class B). Not now declines this
 * ring only. The ring expires with the ping window and never shows after.
 *
 * Layer Z_RING: above sheets, below gates; while a gate is up the sheet
 * renders nothing but stays mounted, so an Accept parked behind the gate
 * finishes when the gate is answered. Focus-managed like the #988 sheets:
 * focus in, Tab held inside, Escape = Not now, focus back after.
 */
export default function RingSheet({ ring, hidden }: Props) {
  const navigate = useNavigate();
  const markHandled = useRingStore((s) => s.markHandled);
  const markAccepted = useRingStore((s) => s.markAccepted);
  const restore = useRingStore((s) => s.restore);
  const declineFailure = useRingStore((s) => s.failed[ring.id] ?? null);
  const [phase, setPhase] = useState<'idle' | 'accepting' | 'refused'>('idle');
  const [message, setMessage] = useState<string | null>(null);
  const titleId = useId();
  const bodyId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const inFlight = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // On screen, the sheet IS the ringer: the ring's own notification goes
  // (#1009 item 1, class D of the incoming call's). Not while a gate hides it.
  useEffect(() => {
    if (hidden) return;
    void cancelRingNotification(ring.id);
  }, [hidden, ring.id]);

  // The ring window (server-relative seconds): an expired ring never shows.
  useEffect(() => {
    if (phase !== 'idle') return;
    const timer = setTimeout(() => markHandled(ring.id), Math.max(0, ring.seconds_left) * 1000);
    return () => clearTimeout(timer);
  }, [ring.id, ring.seconds_left, phase, markHandled]);

  const refreshFeed = () => {
    void useActiveCohortSessionsStore.getState().refresh();
  };

  const notNow = () => {
    if (phase === 'accepting') return;
    markHandled(ring.id);
    if (phase === 'idle') {
      void declineRing(ring.id)
        .then((outcome) => {
          // Never reached the server: the ring is still unanswered there, so
          // it comes back with why (a refusal — answered, not found — is final).
          if (!outcome.success && outcome.error === 'network') restore(ring.id, outcome.message);
        })
        .finally(refreshFeed);
    }
  };

  const accept = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setPhase('accepting');
    setMessage(null);
    // Session-bound, as a cohort card's claim is (#940): the answer WRITES the
    // seat below, and an answer for an account that has since signed out (in
    // another tab, or its session expired) must not land in the next one's.
    const settled = await withSession(() => acceptRing(ring.id)).finally(() => {
      inFlight.current = false;
    });
    if (settled.status === 'ended') return;
    if (settled.status === 'failed') {
      // acceptRing returns its refusals; a throw is a bug, said plainly.
      if (!mounted.current) return;
      setPhase('refused');
      setMessage("Couldn't start the call. Try again.");
      refreshFeed();
      return;
    }
    const outcome = settled.value;
    if (outcome.success) {
      markAccepted(ring.id);
      // THE SEAT IS THE CALL THIS ACCEPT ANSWERED, before the 1:1 page mounts,
      // as a cohort card's Call does (R14). Navigating alone left the store on
      // whatever the last call left: a call that ended while the listener was
      // off the page leaves its End pending, and the flow that mounted for
      // THIS call ran it, leaving the queue (which completes this call on the
      // server) or rejoining it under the live call.
      useEmpathyQueueStore
        .getState()
        .adoptClaimedCall(String(outcome.data.session_id), outcome.data);
      void navigate(LIVE_CALL_ROUTE);
      refreshFeed();
      return;
    }
    // The upgrade prompt already answered it (it hides this sheet while up):
    // the ring is answered, with no refused phase waiting behind the prompt.
    if (outcome.globallyHandled) {
      markHandled(ring.id);
      refreshFeed();
      return;
    }
    if (!mounted.current) return;
    setPhase('refused');
    setMessage(outcome.message);
    refreshFeed();
  };

  useDialogFocus(!hidden, panelRef, {
    onEscape: () => {
      if (phase === 'refused') markHandled(ring.id);
      else notNow();
    },
    // Android Back never declines a ring (#1161 review, David): the sheet is
    // answered with its buttons, as the ping modal and the post-call screens
    // are. A reflex Back or a gesture-nav swipe must not tell someone asking
    // for empathy "not now". Back is swallowed; the page behind stays.
    onBack: () => undefined,
    yieldToOtherModals: true,
  });

  if (hidden) return null;

  // Framed by origin (the ring's scope, as the server set it).
  const seeker = ring.seeker_first_name ?? 'Someone';
  const copy =
    ring.scope === 'group' && ring.group
      ? {
          title: RING_COPY.groupTitle(seeker, ring.group.name),
          line: RING_COPY.groupBody,
          // The request's own words: in-app only, never in the push (18:40).
          words: ring.request_text ?? null,
          why: RING_COPY.groupWhy(seeker),
        }
      : { title: RING_COPY.title, line: RING_COPY.platform, words: null, why: RING_COPY.why };

  return (
    <div
      className="fixed inset-0 flex items-end sm:items-center justify-center bg-(--ee-scrim)/40 px-4 pb-[max(1rem,env(safe-area-inset-bottom))]"
      style={{ zIndex: Z_RING }}
      data-testid="ring-sheet"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        ref={panelRef}
        tabIndex={-1}
        className="w-full max-w-md rounded-2xl bg-base-100 p-5 shadow-xl flex flex-col gap-3 focus:outline-none"
      >
        <h2 id={titleId} className="m-0 text-xl font-bold">
          {copy.title}
        </h2>
        <div id={bodyId} className="flex flex-col gap-1">
          <p className="m-0 text-[15px] leading-snug">{copy.line}</p>
          {copy.words ? (
            <p className="m-0 text-[15px] leading-snug italic" data-testid="ring-request-text">
              “{copy.words}”
            </p>
          ) : null}
          {phase === 'refused' ? null : (
            <p className="m-0 text-sm text-(--ee-tw-gray-600) leading-snug">{copy.why}</p>
          )}
        </div>
        {phase === 'idle' && declineFailure ? (
          <p className="m-0 text-sm font-semibold" role="alert" data-testid="ring-decline-failed">
            {declineFailure}
          </p>
        ) : null}
        {phase === 'refused' ? (
          <>
            <p className="m-0 text-sm font-semibold" role="status" data-testid="ring-refusal">
              {message}
            </p>
            <button
              type="button"
              className="btn btn-primary min-h-12"
              onClick={() => markHandled(ring.id)}
            >
              {RING_COPY.close}
            </button>
          </>
        ) : (
          <div className="flex flex-col sm:flex-row-reverse gap-2 mt-1">
            <button
              type="button"
              className="btn btn-primary min-h-12 sm:flex-1"
              onClick={() => void accept()}
              disabled={phase === 'accepting'}
            >
              {phase === 'accepting' ? (
                <>
                  <span className="loading loading-spinner loading-sm" aria-hidden="true" />
                  {RING_COPY.accepting}
                </>
              ) : (
                RING_COPY.accept
              )}
            </button>
            <button
              type="button"
              className="btn btn-ghost min-h-12 sm:flex-1"
              onClick={notNow}
              disabled={phase === 'accepting'}
            >
              {RING_COPY.notNow}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
